import { useState, useEffect } from 'react'

import api from '@/lib/api'

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const bytes: Uint8Array<ArrayBuffer> = new Uint8Array(rawData.length)
  for (let index = 0; index < rawData.length; index += 1) {
    bytes[index] = rawData.charCodeAt(index)
  }
  return bytes
}

export type PushPermissionState = 'unsupported' | 'default' | 'granted' | 'denied'

export function usePushNotifications() {
  const [permissionState, setPermissionState] = useState<PushPermissionState>('unsupported')
  const [isSubscribing, setIsSubscribing] = useState(false)

  useEffect(() => {
    if (
      !('Notification' in window) ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window)
    ) {
      setPermissionState('unsupported')
      return
    }

    const checkSubscription = async () => {
      const browserPermission = Notification.permission as PushPermissionState

      if (browserPermission !== 'granted') {
        setPermissionState(browserPermission)
        return
      }

      // Permission is granted — check if an active subscription actually exists.
      // If the subscription was lost (e.g. browser storage cleared), re-subscribe silently.
      const registration = await navigator.serviceWorker.register(
        `${import.meta.env.BASE_URL}sw.js`,
        { scope: import.meta.env.BASE_URL },
      )
      await navigator.serviceWorker.ready
      const existingSub = await registration.pushManager.getSubscription()

      if (existingSub) {
        setPermissionState('granted')
      } else {
        // Permission granted but no subscription — re-subscribe and sync with backend
        const { data } = await api.get('/push/vapid-public-key')
        const applicationServerKey = urlBase64ToUint8Array(data.vapidPublicKey)
        const newSub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        })
        await api.post('/push/subscribe', newSub.toJSON()).catch(() => {})
        setPermissionState('granted')
      }
    }

    checkSubscription().catch(() =>
      setPermissionState(Notification.permission as PushPermissionState),
    )
  }, [])

  const subscribe = async (): Promise<boolean> => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false

    setIsSubscribing(true)
    try {
      // Register (or re-use) the service worker
      const registration = await navigator.serviceWorker.register(
        `${import.meta.env.BASE_URL}sw.js`,
        { scope: import.meta.env.BASE_URL },
      )
      await navigator.serviceWorker.ready

      // Ask for notification permission
      const permission = await Notification.requestPermission()
      setPermissionState(permission as PushPermissionState)
      if (permission !== 'granted') return false

      // Fetch VAPID public key from backend
      const { data } = await api.get('/push/vapid-public-key')
      const applicationServerKey = urlBase64ToUint8Array(data.vapidPublicKey)

      // Create the push subscription
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      })

      // Send the subscription to the backend
      await api.post('/push/subscribe', subscription.toJSON())

      return true
    } catch (err) {
      console.error('[push] Failed to subscribe:', err)
      return false
    } finally {
      setIsSubscribing(false)
    }
  }

  const unsubscribe = async (): Promise<void> => {
    if (!('serviceWorker' in navigator)) return
    try {
      const registration = await navigator.serviceWorker.getRegistration('/')
      if (!registration) return

      const subscription = await registration.pushManager.getSubscription()
      if (!subscription) return

      await api.delete('/push/subscribe', { data: { endpoint: subscription.endpoint } })
      await subscription.unsubscribe()
      setPermissionState('default')
    } catch (err) {
      console.error('[push] Failed to unsubscribe:', err)
    }
  }

  return { permissionState, isSubscribing, subscribe, unsubscribe }
}
